/*
 * RangeFacet — progressive-enhancement range slider for numeric metafield list filters.
 *
 * Shopify has no native range filter for metafields: numeric metafields are returned as a
 * `list` of discrete values (URL form `filter.p.m.<ns>.<key>=<value>`). This element wraps the
 * server-rendered checkbox `<ul>`, hides it, and presents a dual-handle slider + From/To inputs
 * that snap to those discrete values. Selecting a range checks every hidden value-checkbox inside
 * it and fires one bubbling `input` event, so Dawn's FacetFiltersForm filters server-side with
 * working pagination and URL state. With JS off, the checkbox list still works.
 *
 * Detection is automatic: activates only when every child value is numeric and there are >= 2 of
 * them. Otherwise it leaves the checkboxes untouched.
 */

// label -> filter.url_to_remove, used to collapse the per-value active-facet pills into one.
const rangeFacetRegistry = new Map();
// Declared here (not below) so connectedCallback's synchronous collapsePills() call at
// customElements.define() time doesn't hit a temporal-dead-zone error.
let collapsing = false;

class RangeFacet extends HTMLElement {
  connectedCallback() {
    if (this.dataset.rfReady) return;

    const checkboxes = Array.from(this.querySelectorAll('input[type="checkbox"]'));
    const numeric = checkboxes.every((input) => {
      const raw = input.value.trim();
      return raw !== '' && !Number.isNaN(Number(raw));
    });
    if (checkboxes.length < 2 || !numeric) return; // not a numeric range — leave checkboxes as-is

    // Distinct values, sorted ascending. Keep the raw string to preserve formatting (e.g. 1.040).
    const seen = new Map();
    checkboxes.forEach((input) => {
      const value = Number(input.value);
      if (!seen.has(value)) seen.set(value, input.value.trim());
    });
    this.values = Array.from(seen.keys()).sort((a, b) => a - b);
    this.rawByValue = seen;
    if (this.values.length < 2) return;

    this.dataset.rfReady = '1';
    this.list = this.querySelector('ul');
    this.label = this.dataset.label || '';
    if (this.label) rangeFacetRegistry.set(this.label, this.dataset.urlToRemove || '');

    this.restoreFromChecked();
    this.buildUI();
    if (this.list) this.list.hidden = true;
    this.hideShowMore();

    collapsePills();
  }

  hideShowMore() {
    const showMore = this.closest('.parent-display, .mobile-facets__submenu')?.querySelector('show-more-button');
    if (showMore) showMore.hidden = true;
  }

  restoreFromChecked() {
    const checkedValues = Array.from(this.querySelectorAll('input[type="checkbox"]:checked')).map((i) =>
      Number(i.value),
    );
    if (checkedValues.length) {
      const lo = Math.min(...checkedValues);
      const hi = Math.max(...checkedValues);
      this.loIndex = this.nearestIndex(lo);
      this.hiIndex = this.nearestIndex(hi);
    } else {
      this.loIndex = 0;
      this.hiIndex = this.values.length - 1;
    }
  }

  nearestIndex(value) {
    let best = 0;
    let bestDist = Infinity;
    this.values.forEach((v, i) => {
      const dist = Math.abs(v - value);
      if (dist < bestDist) {
        bestDist = dist;
        best = i;
      }
    });
    return best;
  }

  raw(index) {
    return this.rawByValue.get(this.values[index]);
  }

  buildUI() {
    const max = this.values.length - 1;
    const fromLabel = this.dataset.fromLabel || 'From';
    const toLabel = this.dataset.toLabel || 'To';

    const controls = document.createElement('div');
    controls.className = 'range-facet__controls';
    controls.innerHTML = `
      <div class="range-facet__slider">
        <div class="range-facet__rail"></div>
        <div class="range-facet__fill"></div>
        <input type="range" class="range-facet__range range-facet__range--min" min="0" max="${max}" step="1" value="${this.loIndex}" aria-label="${fromLabel}">
        <input type="range" class="range-facet__range range-facet__range--max" min="0" max="${max}" step="1" value="${this.hiIndex}" aria-label="${toLabel}">
      </div>
      <div class="range-facet__inputs">
        <div class="range-facet__field">
          <label class="range-facet__label">${fromLabel}</label>
          <input type="number" inputmode="decimal" class="range-facet__number range-facet__number--min" value="${this.raw(this.loIndex)}" min="${this.raw(0)}" max="${this.raw(max)}">
        </div>
        <div class="range-facet__field">
          <label class="range-facet__label">${toLabel}</label>
          <input type="number" inputmode="decimal" class="range-facet__number range-facet__number--max" value="${this.raw(this.hiIndex)}" min="${this.raw(0)}" max="${this.raw(max)}">
        </div>
      </div>`;

    this.insertBefore(controls, this.list);

    this.fill = controls.querySelector('.range-facet__fill');
    this.rangeMin = controls.querySelector('.range-facet__range--min');
    this.rangeMax = controls.querySelector('.range-facet__range--max');
    this.numberMin = controls.querySelector('.range-facet__number--min');
    this.numberMax = controls.querySelector('.range-facet__number--max');

    // Slider/number events drive our own state; stop them reaching Dawn's form listener so it only
    // re-filters when WE dispatch a curated event on commit (not on every drag tick).
    const swallow = (e) => e.stopPropagation();

    this.rangeMin.addEventListener('input', (e) => {
      swallow(e);
      this.loIndex = Math.min(Number(this.rangeMin.value), this.hiIndex);
      this.rangeMin.value = this.loIndex;
      this.syncInputs();
    });
    this.rangeMax.addEventListener('input', (e) => {
      swallow(e);
      this.hiIndex = Math.max(Number(this.rangeMax.value), this.loIndex);
      this.rangeMax.value = this.hiIndex;
      this.syncInputs();
    });
    this.rangeMin.addEventListener('change', (e) => {
      swallow(e);
      this.commit();
    });
    this.rangeMax.addEventListener('change', (e) => {
      swallow(e);
      this.commit();
    });

    this.numberMin.addEventListener('change', (e) => {
      swallow(e);
      this.loIndex = Math.min(this.nearestIndex(Number(this.numberMin.value)), this.hiIndex);
      this.syncInputs();
      this.commit();
    });
    this.numberMax.addEventListener('change', (e) => {
      swallow(e);
      this.hiIndex = Math.max(this.nearestIndex(Number(this.numberMax.value)), this.loIndex);
      this.syncInputs();
      this.commit();
    });
    // Keep typed input from bubbling while the user is mid-edit.
    this.numberMin.addEventListener('input', swallow);
    this.numberMax.addEventListener('input', swallow);

    this.syncInputs();
  }

  syncInputs() {
    const max = this.values.length - 1;
    this.rangeMin.value = this.loIndex;
    this.rangeMax.value = this.hiIndex;
    this.numberMin.value = this.raw(this.loIndex);
    this.numberMax.value = this.raw(this.hiIndex);
    const leftPct = (this.loIndex / max) * 100;
    const rightPct = (this.hiIndex / max) * 100;
    this.fill.style.left = `${leftPct}%`;
    this.fill.style.width = `${rightPct - leftPct}%`;
  }

  commit() {
    const lo = this.values[this.loIndex];
    const hi = this.values[this.hiIndex];
    const isFullRange = this.loIndex === 0 && this.hiIndex === this.values.length - 1;

    // Re-query checkboxes each time: Dawn may have swapped the <ul> on a prior AJAX re-filter.
    const checkboxes = Array.from(this.querySelectorAll('input[type="checkbox"]'));
    checkboxes.forEach((input) => {
      const value = Number(input.value);
      input.checked = !isFullRange && value >= lo && value <= hi;
    });

    if (checkboxes[0]) {
      checkboxes[0].dispatchEvent(new Event('input', { bubbles: true }));
    }
  }
}

customElements.define('range-facet', RangeFacet);

/*
 * Pill collapsing — a range that checks N values yields N active-facet pills. Replace each such
 * group with a single "Label: lo–hi" pill whose remove link clears the whole metafield filter.
 * Dawn re-renders the active-facets containers (innerHTML) on every filter change, so a scoped
 * MutationObserver re-applies this after each render.
 */
function collapsePills() {
  if (collapsing || rangeFacetRegistry.size === 0) return;
  collapsing = true;

  document.querySelectorAll('.active-facets').forEach((container) => {
    const pills = Array.from(container.querySelectorAll(':scope > facet-remove'));

    rangeFacetRegistry.forEach((removeUrl, label) => {
      const prefix = `${label}:`;
      // Exclude already-collapsed pills: collapsing removes DOM nodes, which re-fires the observer;
      // re-parsing the "lo–hi" text would give lo=hi. A genuine re-render brings fresh unmarked pills.
      const group = pills.filter((pill) => !pill.dataset.rfCollapsed && pill.textContent.trim().startsWith(prefix));
      if (group.length === 0) return;

      // Pill text is "Label: value … Remove filter" (incl. a visually-hidden label), so parseFloat
      // (stops at the first non-numeric char) extracts the value; Number() would yield NaN.
      const numbers = group
        .map((pill) => parseFloat(pill.textContent.trim().slice(prefix.length)))
        .filter((n) => !Number.isNaN(n));
      if (numbers.length === 0) return;

      const lo = Math.min(...numbers);
      const hi = Math.max(...numbers);

      const first = group[0];
      const link = first.querySelector('a');
      const inner = first.querySelector('.active-facets__button-inner');
      const textNode =
        inner && Array.from(inner.childNodes).find((n) => n.nodeType === Node.TEXT_NODE && n.textContent.trim());
      if (link && removeUrl) link.setAttribute('href', removeUrl);
      if (textNode) textNode.textContent = ` ${label}: ${lo}–${hi} `;
      first.dataset.rfCollapsed = '1';

      group.slice(1).forEach((pill) => pill.remove());
    });
  });

  collapsing = false;
}

function observePills() {
  const observer = new MutationObserver(() => collapsePills());
  document.querySelectorAll('.active-facets').forEach((container) => {
    observer.observe(container, { childList: true, subtree: true });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', observePills);
} else {
  observePills();
}
